import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'

// A floating list anchored to whatever opened it. It owns its own dismissal -- backdrop click,
// scroll, Escape -- so the step keeps no ref per popover.

const MOBILE_MAX_WIDTH = 600
// Leaves room for the list itself when it opens near the bottom of a phone screen.
const MOBILE_BOTTOM_ALLOWANCE = 240

const PickerPopover = ({ rect, onClose, mobileSheet = false, matchWidth = false, manageFocus = true, children }) => {
  const listRef = useRef(null)
  // Captured during render, before the list autofocuses its search box, so it is the
  // element that opened the popover — focus returns there when the popover closes.
  const triggerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null)

  useEffect(() => {
    const closeIfOutside = (event) => {
      if (!listRef.current?.contains(event.target)) { onClose() }
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') { onClose() }
    }
    window.addEventListener('scroll', closeIfOutside, true)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('scroll', closeIfOutside, true)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [ onClose ])

  // Portalled to the end of the body, so the keyboard has to be put inside on open. The container
  // takes it, not the first row: focusing a row selects it, ringing the first option on a click.
  useEffect(() => {
    if (!manageFocus || listRef.current?.contains(document.activeElement)) { return }
    listRef.current?.focus()
  }, [])

  // Restore focus to the trigger only on unmount, so a re-render while open does not
  // steal focus away from the list's search box.
  useEffect(() => () => { triggerRef.current?.focus?.() }, [])

  // On a phone the list spans the viewport instead of hanging off the seat, which
  // would otherwise overflow the screen.
  const asSheet = mobileSheet && window.innerWidth <= MOBILE_MAX_WIDTH
  const position = asSheet
    ? {
      top: Math.min(rect.bottom + 4, window.innerHeight - MOBILE_BOTTOM_ALLOWANCE),
      left: 12,
      right: 12,
    }
    // A combobox's list belongs under its own field, edge to edge; a seat menu sizes to its
    // own content instead.
    : { top: rect.bottom + 4, left: rect.left, ...(matchWidth ? { width: rect.width } : {}) }

  return createPortal(
    <>
      <div
        className={`gl-event-setup-popover-backdrop${asSheet ? ' is-dim' : ''}`}
        onClick={onClose}
      />
      <div ref={listRef} className="gl-event-setup-slot-picker-list" style={position} tabIndex={-1}>
        {children}
      </div>
    </>,
    document.body,
  )
}

PickerPopover.propTypes = {
  // Bounding box of the seat the list hangs from
  rect: PropTypes.shape({
    bottom: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    width: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  mobileSheet: PropTypes.bool,
  // Take the trigger's width rather than the content's.
  matchWidth: PropTypes.bool,
  // False for a combobox: it keeps focus on its own input, so aria-activedescendant still means something.
  manageFocus: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

export default PickerPopover
